import { Avatar, SelectionList, Text } from '@dixa/lib-shared-habla';
import { sizes } from '@dixa/lib-shared-tokens';
import * as Popover from '@radix-ui/react-popover';
import classNames from 'classnames';
import { getLoginURL } from 'lib-shared-auth';
import { ampli } from 'lib-shared-tracking';
import type { Organization } from 'lib-shared-types-api';
import { useCallback, useState } from 'react';

import { loginApi } from 'apis/login';
import { useAppSelector } from 'utils/redux/useAppSelector';

import styles from './organizationSwitcher.module.scss';

export const OrganizationSwitcher = (): JSX.Element | null => {
  const organizations = useAppSelector((state) => state.app.organizations);
  const currentOrganization = useAppSelector((state) => state.app.organization);

  const [isOpen, setIsOpen] = useState(false);

  const [createLoginToken] = loginApi.useCreateLoginTokenMutation();

  const openOrganization = useCallback(
    async (organization: Organization) => {
      ampli.organizationsNavigationOrganizationClicked({
        id: organization.id,
        name: organization.name,
        subdomain: organization.subdomain,
      });

      const { data: loginTokenData } = await createLoginToken().unwrap();

      const navigateToUrl = getLoginURL({
        loginToken: loginTokenData.login_token,
        organization,
        url: new URL(window.location.href),
      });

      window.location.href = navigateToUrl;
    },
    [createLoginToken],
  );

  if (!currentOrganization || organizations.length <= 1) return null;

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={styles.trigger}
          aria-label={currentOrganization.name}
        >
          <Avatar
            size="32"
            user={{
              id: currentOrganization.id,
              avatar_url: currentOrganization.avatar_url,
              name: currentOrganization.name,
            }}
            className={styles.avatar}
          />
        </button>
      </Popover.Trigger>

      <Popover.Content
        className={styles.popover}
        side="bottom"
        align="start"
        sideOffset={parseInt(sizes.spacer.values[200])}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <SelectionList
          className={styles.list}
          selectedValues={[currentOrganization]}
          isActive={isOpen}
          equalityFn={(a, b) => a.id === b.id}
          onChange={({ selectedValues }) => {
            const org = selectedValues[0];

            if (org && org.id !== currentOrganization.id) {
              setIsOpen(false);
              openOrganization(org);
            }
          }}
        >
          {organizations.map((organization) => (
            <SelectionList.Item
              key={organization.id}
              value={organization}
              className={classNames(styles.listItem, {
                [styles.listItemActive]:
                  organization.id === currentOrganization.id,
              })}
              contentClassName={styles.listItemContent}
            >
              <Avatar
                size="32"
                user={{
                  id: organization.id,
                  avatar_url: organization.avatar_url,
                  name: organization.name,
                }}
                className={styles.avatar}
              />

              <div className={styles.orgInfo}>
                <Text.Paragraph variant="1" as="span">
                  {organization.name}
                </Text.Paragraph>

                <Text.Paragraph variant="2" as="span">
                  {organization.subdomain}.dixa.com
                </Text.Paragraph>
              </div>
            </SelectionList.Item>
          ))}
        </SelectionList>
      </Popover.Content>
    </Popover.Root>
  );
};
